import type { Image, MultilingualLabel } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import { useEffect } from 'react';
import type { UseFormClearErrors, UseFormReset, UseFormWatch } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { fetchCampaignById, saveCampaign } from 'Store/entities/activity/activity.thunks';
import type {
  ActivityAudience,
  Campaign as CampaignType,
  PartialContentForCampaignForm,
} from 'Store/entities/activity/activity.type';
import { convertPartialContentFromCampaignForm } from 'Store/entities/content/content.utils';

import { AudienceFormValues, saveAudience, type AudienceFormType } from '../AudienceForm/audience.utils';

const ALERT_DURATION = 5000;

export type CampaignFormValues = {
  mode: 'manual' | 'random';
  type: 'intense' | 'flexible';
  name?: Partial<MultilingualLabel>;
  coverImage?: Pick<Image, 'id' | 'url'>;
  description?: Partial<MultilingualLabel>;
  disclaimer?: Partial<MultilingualLabel>;
  launchDate?: string;
  endDate?: string;
  extraDays?: number;
  enabledWeekDays?: number[];
  disabledDates?: string[];
  nbSessions?: number;
  orderedSessions?: PartialContentForCampaignForm[][];
  contents?: PartialContentForCampaignForm[];
} & AudienceFormType & { audienceDirty: boolean };

type UseCampaignTypeProps = {
  campaign: CampaignType;
  dirty: boolean;
  defaultValues: CampaignFormValues;
  setVisibleSaveModal: (visible: boolean) => void;
  watch: UseFormWatch<CampaignFormValues>;
  reset: UseFormReset<CampaignFormValues>;
  clearErrors: UseFormClearErrors<CampaignFormValues>;
};

export const useCampaignSave = ({
  campaign,
  dirty,
  watch,
  defaultValues,
  setVisibleSaveModal,
  reset,
  clearErrors,
}: UseCampaignTypeProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    reset({ ...defaultValues });
  }, [campaign]);

  const handleCampaignAudienceSave = async (
    audiencesFormValues: AudienceFormValues,
    hasAudienceChanged: boolean,
    currentAudience?: ActivityAudience[],
  ) => {
    try {
      if (!audiencesFormValues) return;

      await saveAudience({
        hasAudienceChanged,
        audienceFormValues: audiencesFormValues,
        activityAudience: currentAudience,
        entity: {
          type: 'activity',
          id: campaign.id,
        },
        dispatch,
      });
      enqueueBasicAlert(
        {
          id: 'campaign_saved',
          status: 'success',
          title: t('activities:alerts.success.save'),
        },
        ALERT_DURATION,
      );
      dispatch(fetchCampaignById(campaign.id));
      setVisibleSaveModal(false);
    } catch (error: any) {
      const errorText =
        error?.errorCode === 'AUD-CMC-CMC-001'
          ? t('api-errors:magic_code_already_exists')
          : t('activities:alerts.errors.save_error_message', {
              message: t('activities:alerts.errors.save_error_title'),
            });

      enqueueBasicAlert(
        {
          id: 'campaign_not_saved',
          status: 'error',
          title: errorText,
        },
        ALERT_DURATION,
      );
    }
  };

  const handleCampaignLive = async () => {
    clearErrors();
    const { unwrap } = dispatch(
      saveCampaign({
        ...campaign,
        status: campaign.status === 'draft' ? 'live' : 'draft',
      }),
    );

    try {
      await unwrap();

      enqueueBasicAlert(
        {
          id: 'campaign_saved',
          status: 'success',
          title: t('activities:alerts.success.save'),
        },
        ALERT_DURATION,
      );
    } catch (error: unknown) {
      enqueueBasicAlert(
        {
          id: 'campaign_not_saved',
          status: 'error',
          title: t('activities:alerts.errors.activate_error_message', {
            message: t('activities:alerts.errors.validation_error_title'),
          }),
        },
        ALERT_DURATION,
      );
    }
  };

  const handleCampaignSave = async () => {
    clearErrors();
    const formValues = watch();
    const audienceDirty = watch('audienceDirty');

    const { audiences, ...restOfCampaignValues } = campaign;

    const newContents = formValues.contents?.map(convertPartialContentFromCampaignForm);

    const orderedSessions = formValues.orderedSessions?.map((session) =>
      session.map(convertPartialContentFromCampaignForm),
    );

    const { unwrap: unwrapUpdateCampaign } = dispatch(
      saveCampaign({
        ...restOfCampaignValues,
        id: campaign.id,
        informations: {
          ...campaign.informations,
          name: formValues.name,
          description: formValues.description,
          disclaimer: formValues.disclaimer,
          coverImage: formValues.coverImage,
        },
        scheduling: {
          ...campaign.scheduling,
          type: formValues.type,
          mode: formValues.mode,
          launchDate: formValues.launchDate,
          endDate: formValues.endDate,
          extraDays: formValues.extraDays,
          enabledWeekDays: formValues.enabledWeekDays,
          disabledDates: formValues.disabledDates,
          orderedSessions: orderedSessions,
          contents: newContents,
          nbSessions: formValues.nbSessions,
        },
      }),
    );

    try {
      if (dirty) {
        await unwrapUpdateCampaign();
      }

      if (audienceDirty) {
        const hasAudienceChanged = watch('audienceTypeChanged');
        await handleCampaignAudienceSave(formValues.audiences, hasAudienceChanged, campaign.audiences);
      }

      if (dirty || audienceDirty) {
        enqueueBasicAlert(
          {
            id: 'campaign_saved',
            status: 'success',
            title: t('activities:alerts.success.save'),
          },
          ALERT_DURATION,
        );
      }
    } catch (error: unknown) {
      enqueueBasicAlert(
        {
          id: 'campaign_not_saved',
          status: 'error',
          title: t('activities:alerts.errors.save_error_message', {
            message: t('activities:alerts.errors.save_error_title'),
          }),
        },
        ALERT_DURATION,
      );
    }
  };

  const handleClickOnSave = async () => {
    // confirmation modal should be visible only if audience is changed
    // not if it's the first time the audience is saved
    if (watch('audienceDirty') && campaign.audiences && campaign.audiences.length > 0) {
      setVisibleSaveModal(true);
    } else {
      await handleCampaignSave();
    }
  };

  return { handleCampaignSave: handleClickOnSave, handleCampaignLive, handleCampaignAudienceSave };
};
